/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/indent */
import { createApi } from '@reduxjs/toolkit/query/react';
import { TServerResponse } from 'app/types/server-response';
import { logisticsAPIBase } from 'app/utilities/env';
import getBaseQueryWithLogout from 'app/utilities/getBaseQuery';
import {
  LogisticsCharterRequestDTO,
  LogisticsCharterRequestResponse,
} from 'features/booking/forms/charter';
import { TOrder } from 'features/orders/types/order';
import { WaybillData } from 'features/orders/types/parcel';
import { RequestQuoteDTO } from 'features/website-layout/types/quote';

import {
  CalculateOrderDTO,
  CheckLogisticsCharterDTO,
  LogisticsCategory,
  LogisticsCharterDTO,
  LogisticsCharterResponse,
  LogisticsRequestDTO,
  LogisticsRequestServerResponse,
  LogisticsSubCategory,
  RateCalculateDTO,
  RateCalculateServerResponse,
  TLogisticsFleet,
} from '../types/logistics';
import { Station } from '../types/terminals';

export const logisticsApiSlice = createApi({
  reducerPath: 'logisticsApi',
  baseQuery: getBaseQueryWithLogout(logisticsAPIBase),
  endpoints: (build) => ({
    requestCharter: build.mutation<
      LogisticsCharterResponse,
      LogisticsCharterDTO
    >({
      query: (body) => ({
        url: '/charter',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: TServerResponse<LogisticsCharterResponse>
      ) => response.data,
    }),

    getCategories: build.query<LogisticsCategory[], void>({
      query: () => '/categories',
      transformResponse: (res: TServerResponse<LogisticsCategory[]>) =>
        res.data,
    }),

    getSubCategories: build.query<LogisticsSubCategory[], void>({
      query: () => '/subcategories',
      transformResponse: (res: TServerResponse<LogisticsSubCategory[]>) =>
        res.data,
    }),

    getSubCategoriesByCategory: build.query<LogisticsSubCategory[], string>({
      query: (id) => `/categories/${id}/subcategories`,
      transformResponse: (res: TServerResponse<LogisticsSubCategory[]>) =>
        res.data,
    }),

    saveLogisticsOrder: build.mutation<
      LogisticsRequestServerResponse,
      LogisticsRequestDTO
    >({
      query: (body) => ({
        url: '/save-order',
        method: 'POST',
        body,
      }),
      transformResponse: (
        res: TServerResponse<LogisticsRequestServerResponse>
      ) => res.data,
    }),
    trackShipment: build.query<WaybillData, string>({
      query: (id) => `https://business.guologistics.com/api/waybill/${id}`,
      transformResponse: (res: WaybillData) => res,
    }),
    trackShipmentPrev: build.query<TOrder, string>({
      query: (id) => `https://business.guologistics.com/api/waybill/${id}`,
      transformResponse: (res: any) : TOrder => {
        const data = res.waybill;
        return {
          pickUpAddress: {
            address: data.terminal_sender.terminal_name,
            longitude: data.terminal_sender?.terminal_longitude || '',
            latitude: data.terminal_sender?.terminal_latitude || ''
          },
          dropOffAddress: {
            address: data.terminal_receiver.terminal_name,
            longitude: data.terminal_receiver?.terminal_longitude || '',
            latitude: data.terminal_receiver?.terminal_latitude || ''
          },
          otherPhoneNumber: [data.consignee.consign_phonenumber],
          type: data.waybill_completed_type,
          status: [
            {
              dateTime: data.waybill_avail_collectiondate,
              location: data.terminal_receiver.terminal_name,
              user: data.waybill_collected_by,
              innerTrackingId: data.waybill_no,
              description: res.waybill_status
            }
          ],
          id: data.waybill_id,
          trackingId: data.waybill_no,
          dispatchType: data.waybill_send_type_id.toString(),
          stationId: data.waybill_endter.toString(),
          senderEmail: data.consignor?.consign_email || '',
          senderName: data.consignor.full_name,
          senderPhoneNumber: data.consignor.consign_phonenumber,
          receiverName: data.consignee.full_name,
          receiverEmail: data.consignee?.consign_email || '',
          receiverPhoneNumber: data.consignee.consign_phonenumber,
          driverId: null,
          isInstant: false,
          isScheduled: Boolean(data.pickup_address),
          flexibleSchedule: Boolean(data.is_interterminal),
          itemWeight: parseFloat(data.items[0]?.item_weight || '0'),
          amount: data.totals_expected,
          category: data.items[0]?.category_id || 0,
          subCategory: data.items[0]?.subcategory_id || 0,
          width: 0,
          height: 0,
          pickUpDate: data.waybill_collectable_date,
          deliveryDate: data.waybill_collectable_date,
          userId: null,
          paymentId: data.payment_method?.toString() || null,
          reviews: null,
          rating: null,
          createdBy: 0,
          isViewed: data.receiver_sms_sent ? 1 : 0,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          payment: {
            id: data.waybill_no,
            service: 'Logistics',
            paymentChannel: data.payment_method?.toString() || 'Unknown',
            transactionRef: data.waybill_no,
            transactionId: data.waybill_no,
            amount: data.totals_expected,
            userId: data.waybill_sender_payment_type ? 1 : 0,
            firstName: data.consignor.consign_firstname,
            lastName: data.consignor.consign_lastname,
            paymentStatus: data.waybill_status,
            paymentMethod: data.payment_method?.toString() || 'Unknown',
            paymentGateway: 'GUO Transport',
            deletedAt: null,
            createdAt: data.created_at,
            updatedAt: data.updated_at
          },
          rider: {
            id: data.transferred_customer_staff_id?.toString() || '0',
            firstName: 'Rider',
            lastName: 'Unknown',
            email: '',
            phoneNumber: data.waybill_collected_by,
            trips: data.trips.length.toString(),
            status: 'Active',
            profileImage: '',
            createdBy: 'System',
            createdAt: data.created_at,
            updatedAt: data.updated_at
          },
          items: data.items.map((item: any) => ({
            images: [],
            id: item.item_id,
            dispatchOrderId: 0,
            itemName: item.item_description,
            category: item.category_id,
            subCategory: item.subcategory_id.toString(),
            itemQuantity: item.item_quantity,
            itemValue: item.item_value,
            description: item.item_description,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          }))
        };
      }
    }),
    calculateEstimate: build.mutation<
      RateCalculateServerResponse,
      RateCalculateDTO
    >({
      query: (body) => ({
        url: '/estimate',
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<RateCalculateServerResponse>) =>
        res.data,
    }),
    calculateOrder: build.mutation<number, CalculateOrderDTO>({
      query: (body) => ({
        url: '/calculate',
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<number>) => res.data,
    }),

    getStations: build.query<Station[], void>({
      query: () => 'https://business.guologistics.com/api/stations',
      transformResponse: (response: any) => {
        const stateMap = new Map(
          response.states.map((state: any) => [
            Number(state.StateID),
            state.StateName,
          ])
        );
        return response.terminals.map((item: any) => ({
          id: item.LoadinID,
          countryId: item.StateID,
          stateId: item.StateID,
          station: [
            item.Loading_Office,
            stateMap.get(Number(item.StateID)),
          ].filter(Boolean).join(', '),
          address: item.logistics_address,
          phoneNumber: item.courier_phone,
          courierWhatsappPhone: item.courier_whatsapp_phone,
          imageLink: null,
          latitude: item.Latitude,
          longitude: item.Longitude,
          createdBy: 0,
          createdAt: item.Datecreated,
          updatedAt: item.ModifiedLast,
          isInternational: Boolean(item.isInterTerminal),
        }));
      },
    }),

    charterRequest: build.mutation<
      LogisticsCharterRequestResponse,
      LogisticsCharterRequestDTO
    >({
      query: (body) => ({
        url: '/charter',
        method: 'POST',
        body,
      }),
      transformResponse: (
        res: TServerResponse<LogisticsCharterRequestResponse>
      ) => res.data,
    }),

    negotiateCharter: build.mutation<unknown, LogisticsCharterRequestDTO>({
      query: (body) => ({
        url: '/charter/negotiate',
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<unknown>) => res.data,
    }),

    getUserOrders: build.query<TOrder[], number>({
      query: (id) => `/orders/${id}`,
      transformResponse: (res: TServerResponse<TOrder[]>) => res.data,
    }),

    requestQuote: build.mutation<unknown, RequestQuoteDTO>({
      query: (body) => ({
        url: '/get-quote',
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<unknown>) => res.data,
    }),

    checkCharter: build.mutation<TLogisticsFleet[], CheckLogisticsCharterDTO>({
      query: (body) => ({
        url: '/charter/check',
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<TLogisticsFleet[]>) => res.data,
    }),

    getFleets: build.query<TLogisticsFleet[], void>({
      query: () => '/fleets',
      transformResponse: (res: TServerResponse<TLogisticsFleet[]>) => res.data,
    }),
  }),
});

export const {
  useRequestCharterMutation,
  useGetCategoriesQuery,
  useGetSubCategoriesQuery,
  useLazyGetCategoriesQuery,
  useLazyGetStationsQuery,
  useLazyGetSubCategoriesQuery,
  useSaveLogisticsOrderMutation,
  useCalculateEstimateMutation,
  useGetStationsQuery,
  useCharterRequestMutation,
  useCalculateOrderMutation,
  useTrackShipmentQuery,
  useLazyTrackShipmentQuery,
  useTrackShipmentPrevQuery,
  useLazyTrackShipmentPrevQuery,
  useGetUserOrdersQuery,
  useLazyGetUserOrdersQuery,
  useRequestQuoteMutation,
  useGetSubCategoriesByCategoryQuery,
  useLazyGetSubCategoriesByCategoryQuery,
  useCheckCharterMutation,
  useNegotiateCharterMutation,
  useGetFleetsQuery,
  useLazyGetFleetsQuery,
} = logisticsApiSlice;
